import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { AgGridReact } from 'ag-grid-react';
import {
  Button,
  Tag,
  message,
  Input,
  Space,
  Popconfirm,
  Checkbox,
  Switch,
} from 'antd';
import {
  EditOutlined,
  CheckCircleOutlined,
  StopOutlined,
  SearchOutlined,
  ReloadOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  DownOutlined,
  UnorderedListOutlined,
  GlobalOutlined,
  DeleteOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import EditProductDrawer from '../components/Products/EditProductDrawer';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const deptNames = {
  '01': 'Ain',
  '02': 'Aisne',
  '03': 'Allier',
  '04': 'Alpes-de-Haute-Provence',
  '05': 'Hautes-Alpes',
  '06': 'Alpes-Maritimes',
  '07': 'Ardèche',
  '08': 'Ardennes',
  '09': 'Ariège',
  10: 'Aube',
  11: 'Aude',
  12: 'Aveyron',
  13: 'Bouches-du-Rhône',
  14: 'Calvados',
  15: 'Cantal',
  16: 'Charente',
  17: 'Charente-Maritime',
  18: 'Cher',
  19: 'Corrèze',
  21: "Côte-d'Or",
  22: "Côtes-d'Armor",
  23: 'Creuse',
  24: 'Dordogne',
  25: 'Doubs',
  26: 'Drôme',
  27: 'Eure',
  28: 'Eure-et-Loir',
  29: 'Finistère',
  30: 'Gard',
  31: 'Haute-Garonne',
  32: 'Gers',
  33: 'Gironde',
  34: 'Hérault',
  35: 'Ille-et-Vilaine',
  36: 'Indre',
  37: 'Indre-et-Loire',
  38: 'Isère',
  39: 'Jura',
  40: 'Landes',
  41: 'Loir-et-Cher',
  42: 'Loire',
  43: 'Haute-Loire',
  44: 'Loire-Atlantique',
  45: 'Loiret',
  46: 'Lot',
  47: 'Lot-et-Garonne',
  48: 'Lozère',
  49: 'Maine-et-Loire',
  50: 'Manche',
  51: 'Marne',
  52: 'Haute-Marne',
  53: 'Mayenne',
  54: 'Meurthe-et-Moselle',
  55: 'Meuse',
  56: 'Morbihan',
  57: 'Moselle',
  58: 'Nièvre',
  59: 'Nord',
  60: 'Oise',
  61: 'Orne',
  62: 'Pas-de-Calais',
  63: 'Puy-de-Dôme',
  64: 'Pyrénées-Atlantiques',
  65: 'Hautes-Pyrénées',
  66: 'Pyrénées-Orientales',
  67: 'Bas-Rhin',
  68: 'Haut-Rhin',
  69: 'Rhône',
  70: 'Haute-Saône',
  71: 'Saône-et-Loire',
  72: 'Sarthe',
  73: 'Savoie',
  74: 'Haute-Savoie',
  75: 'Paris',
  76: 'Seine-Maritime',
  77: 'Seine-et-Marne',
  78: 'Yvelines',
  79: 'Deux-Sèvres',
  80: 'Somme',
  81: 'Tarn',
  82: 'Tarn-et-Garonne',
  83: 'Var',
  84: 'Vaucluse',
  85: 'Vendée',
  86: 'Vienne',
  87: 'Haute-Vienne',
  88: 'Vosges',
  89: 'Yonne',
  90: 'Territoire de Belfort',
  91: 'Essonne',
  92: 'Hauts-de-Seine',
  93: 'Seine-Saint-Denis',
  94: 'Val-de-Marne',
  95: "Val-d'Oise",
};

const API_URL = process.env.REACT_APP_API_URL;

function StatusBadge({ value }) {
  return value === 'published' ? (
    <Tag color="success" icon={<CheckCircleOutlined />}>
      Publié
    </Tag>
  ) : (
    <Tag color="default" icon={<StopOutlined />}>
      Brouillon
    </Tag>
  );
}

function ActionsCellRenderer({
  data,
  onEdit,
  onToggleStatus,
  onCreatePlaylist,
  onRestore,
}) {
  const navigate = useNavigate();
  const isPublished = data.status === 'published';
  const isDeleted = !!data.deleted_at;

  if (isDeleted) {
    return (
      <Space size="small">
        <Popconfirm
          title="Restaurer ce produit ?"
          description="Le produit sera restauré en brouillon."
          onConfirm={() => onRestore(data)}
          okText="Restaurer"
          cancelText="Annuler"
        >
          <Button size="small" icon={<UndoOutlined />} type="primary">
            Restaurer
          </Button>
        </Popconfirm>
      </Space>
    );
  }

  return (
    <Space size="small">
      <Button
        size="small"
        icon={<EnvironmentOutlined />}
        onClick={() => navigate(`/playlists/new?view=product&id=${data.slug}`)}
      >
        Visualiser
      </Button>
      <Button
        size="small"
        icon={<GlobalOutlined />}
        href={`https://devis-localise.com/produits/${data.slug}`}
        target="_blank"
        disabled={!isPublished}
      >
        Site
      </Button>
      <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(data)}>
        Modifier
      </Button>
      <Popconfirm
        title={isPublished ? 'Dépublier ce produit ?' : 'Publier ce produit ?'}
        description={
          isPublished
            ? 'Il ne sera plus visible sur le site.'
            : 'Il sera immédiatement visible sur le site.'
        }
        onConfirm={() => onToggleStatus(data)}
        okText="Confirmer"
        cancelText="Annuler"
      >
        <Button
          size="small"
          type={isPublished ? 'default' : 'primary'}
          danger={isPublished}
          icon={isPublished ? <StopOutlined /> : <CheckCircleOutlined />}
        >
          {isPublished ? 'Dépublier' : 'Publier'}
        </Button>
      </Popconfirm>
      {data.has_playlist ? (
        <Button
          size="small"
          icon={<CheckCircleOutlined />}
          disabled
          style={{
            color: '#52c41a',
            borderColor: '#b7eb8f',
            background: '#f6ffed',
          }}
        >
          Playlist créée
        </Button>
      ) : (
        <Popconfirm
          title="Créer une playlist ?"
          description={`Créer la playlist "${data.name}" avec ${(data.establishment_count || 0).toLocaleString('fr-FR')} contacts`}
          onConfirm={() => onCreatePlaylist(data)}
          okText="Créer"
          cancelText="Annuler"
        >
          <Button size="small" icon={<UnorderedListOutlined />}>
            Playlist
          </Button>
        </Popconfirm>
      )}
    </Space>
  );
}

export default function PackagedProductsPage() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quickFilter, setQuickFilter] = useState('');
  const [editDrawer, setEditDrawer] = useState({ open: false, product: null });
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [expandedRegions, setExpandedRegions] = useState({});
  const [showDeleted, setShowDeleted] = useState(false);
  const gridRef = useRef(null);

  // Computed filter options from loaded products
  const productsByRegion = useMemo(() => {
    const groups = {};
    products.forEach(p => {
      const region = p.region || 'France entière';
      groups[region] = (groups[region] || 0) + 1;
    });
    return groups;
  }, [products]);

  const productsByDepartment = useMemo(() => {
    const groups = {};
    products.forEach(p => {
      const region = p.region || 'France entière';
      if (!groups[region]) groups[region] = {};
      (p.filter_departments || []).forEach(dept => {
        groups[region][dept] = (groups[region][dept] || 0) + 1;
      });
    });
    return groups;
  }, [products]);

  const productsByCategory = useMemo(() => {
    const groups = {};
    products.forEach(p => {
      const cat = p.category || 'Autre';
      groups[cat] = (groups[cat] || 0) + 1;
    });
    return Object.entries(groups).sort((a, b) => b[1] - a[1]);
  }, [products]);

  const filteredProducts = useMemo(() => {
    let filtered = products;
    if (selectedRegions.length > 0) {
      filtered = filtered.filter(p =>
        selectedRegions.includes(p.region || 'France entière')
      );
    }
    if (selectedDepartments.length > 0) {
      filtered = filtered.filter(p =>
        p.filter_departments?.some(d => selectedDepartments.includes(d))
      );
    }
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(p => selectedCategories.includes(p.category));
    }
    return filtered;
  }, [products, selectedRegions, selectedDepartments, selectedCategories]);

  const hasActiveFilters =
    selectedRegions.length > 0 ||
    selectedDepartments.length > 0 ||
    selectedCategories.length > 0;

  const resetFilters = () => {
    setSelectedRegions([]);
    setSelectedDepartments([]);
    setSelectedCategories([]);
  };

  const toggleRegion = region =>
    setSelectedRegions(prev =>
      prev.includes(region) ? prev.filter(r => r !== region) : [...prev, region]
    );
  const toggleDepartment = dept =>
    setSelectedDepartments(prev =>
      prev.includes(dept) ? prev.filter(d => d !== dept) : [...prev, dept]
    );
  const toggleCategory = cat =>
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  const toggleRegionExpand = region =>
    setExpandedRegions(prev => ({ ...prev, [region]: !prev[region] }));

  const fetchProducts = useCallback(async (deleted = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('prae.jwt');
      const url = `${API_URL}/packaged-products/admin${deleted ? '?showDeleted=true' : ''}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      message.error(`Erreur chargement : ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleToggleStatus = useCallback(async product => {
    const newStatus = product.status === 'published' ? 'draft' : 'published';
    try {
      const token = localStorage.getItem('prae.jwt');
      const res = await fetch(
        `${API_URL}/packaged-products/${product.slug}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      setProducts(prev =>
        prev.map(p => (p.slug === updated.slug ? updated : p))
      );
      message.success(
        newStatus === 'published' ? 'Produit publié !' : 'Produit dépublié.'
      );
    } catch (err) {
      message.error(`Erreur : ${err.message}`);
    }
  }, []);

  const handleEdit = useCallback(product => {
    setEditDrawer({ open: true, product });
  }, []);

  const handleDelete = useCallback(
    async product => {
      try {
        const token = localStorage.getItem('prae.jwt');
        const res = await fetch(
          `${API_URL}/packaged-products/${product.slug}`,
          {
            method: 'DELETE',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        message.success('Produit supprimé.');
        fetchProducts(showDeleted);
      } catch (err) {
        message.error(`Erreur : ${err.message}`);
      }
    },
    [showDeleted, fetchProducts]
  );

  const handleRestore = useCallback(
    async product => {
      try {
        const token = localStorage.getItem('prae.jwt');
        const res = await fetch(
          `${API_URL}/packaged-products/${product.slug}/restore`,
          {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        message.success('Produit restauré en brouillon.');
        fetchProducts(showDeleted);
      } catch (err) {
        message.error(`Erreur : ${err.message}`);
      }
    },
    [showDeleted, fetchProducts]
  );

  React.useEffect(() => {
    fetchProducts(showDeleted);
  }, [showDeleted, fetchProducts]);

  const handleCreatePlaylist = useCallback(
    async product => {
      try {
        const token = localStorage.getItem('prae.jwt');
        const res = await fetch(`${API_URL}/playlists/from-selection`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            name: product.name,
            userId: user?.id,
            depts: product.filter_departments || [],
            communes: product.filter_communes || [],
            communesInsee: product.filter_communes_insee || [],
            naf: product.filter_naf || [],
            emailOnly: product.filter_email_only || false,
            expectedCount: product.establishment_count,
            productSlug: product.slug,
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || `HTTP ${res.status}`);
        }
        const playlist = await res.json();
        message.success(
          `Playlist "${product.name}" créée (${playlist.tracks} contacts)`
        );
        fetchProducts(showDeleted);
      } catch (err) {
        message.error(`Erreur : ${err.message}`);
      }
    },
    [fetchProducts, showDeleted]
  );

  const columnDefs = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Nom',
        flex: 2,
        minWidth: 220,
        cellRenderer: ({ data }) => (
          <div>
            <div style={{ fontWeight: 600, lineHeight: '1.3' }}>
              {data.name}
            </div>
            {data.category && (
              <Tag style={{ fontSize: 10, marginTop: 2 }}>{data.category}</Tag>
            )}
          </div>
        ),
      },
      {
        field: 'slug',
        headerName: 'Slug',
        flex: 1,
        minWidth: 160,
        cellStyle: { fontFamily: 'monospace', fontSize: 12, color: '#666' },
      },
      {
        field: 'establishment_count',
        headerName: 'Contacts',
        width: 110,
        type: 'numericColumn',
        valueFormatter: ({ value }) =>
          value ? value.toLocaleString('fr-FR') : '0',
        cellStyle: { fontWeight: 600 },
      },
      {
        field: 'status',
        headerName: 'Statut',
        width: 120,
        cellRenderer: ({ value }) => <StatusBadge value={value} />,
        filter: true,
      },
      {
        field: 'created_at',
        headerName: 'Créé le',
        width: 130,
        valueFormatter: ({ value }) =>
          value ? new Date(value).toLocaleDateString('fr-FR') : '',
      },
      {
        headerName: 'Actions',
        width: 490,
        sortable: false,
        filter: false,
        cellRenderer: ({ data }) => (
          <ActionsCellRenderer
            data={data}
            onEdit={handleEdit}
            onToggleStatus={handleToggleStatus}
            onCreatePlaylist={handleCreatePlaylist}
            onRestore={handleRestore}
          />
        ),
      },
      {
        headerName: '',
        width: 60,
        sortable: false,
        filter: false,
        cellRenderer: ({ data }) =>
          !data.deleted_at && (
            <Popconfirm
              title="Supprimer ce produit ?"
              description="Le produit sera désactivé et masqué. Vous pourrez le restaurer."
              onConfirm={() => handleDelete(data)}
              okText="Supprimer"
              okButtonProps={{ danger: true }}
              cancelText="Annuler"
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          ),
      },
    ],
    [
      handleEdit,
      handleToggleStatus,
      handleCreatePlaylist,
      handleDelete,
      handleRestore,
    ]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      cellStyle: { display: 'flex', alignItems: 'center' },
    }),
    []
  );

  const publishedCount = filteredProducts.filter(
    p => p.status === 'published'
  ).length;
  const draftCount = filteredProducts.filter(
    p => p.status !== 'published'
  ).length;

  return (
    <div
      style={{
        paddingTop: 48,
        paddingLeft: 24,
        paddingRight: 24,
        paddingBottom: 24,
        height: 'calc(100vh - 140px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
            Produits packagés
          </h2>
          <div style={{ marginTop: 4, fontSize: 13, color: '#666' }}>
            {filteredProducts.length} produits
            {hasActiveFilters ? ` (sur ${products.length})` : ''} ·{' '}
            <span style={{ color: '#52c41a', fontWeight: 600 }}>
              {publishedCount} publiés
            </span>{' '}
            ·{' '}
            <span style={{ color: '#888', fontWeight: 600 }}>
              {draftCount} brouillons
            </span>
            {hasActiveFilters && (
              <Button
                type="link"
                size="small"
                onClick={resetFilters}
                style={{ padding: '0 4px' }}
              >
                Réinitialiser les filtres
              </Button>
            )}
          </div>
        </div>
        <Space>
          <Input
            placeholder="Rechercher..."
            prefix={<SearchOutlined />}
            value={quickFilter}
            onChange={e => setQuickFilter(e.target.value)}
            style={{ width: 220 }}
            allowClear
          />
          <Space>
            <Switch
              checked={showDeleted}
              onChange={setShowDeleted}
              checkedChildren="Supprimés"
              unCheckedChildren="Actifs"
            />
          </Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchProducts(showDeleted)}
            loading={loading}
          >
            Actualiser
          </Button>
        </Space>
      </div>

      {/* Body: filters + grid */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 16 }}>
        {/* Sidebar Filters */}
        <div
          style={{
            width: 240,
            flexShrink: 0,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: '12px 0',
          }}
        >
          {/* Localisation */}
          <div
            style={{
              padding: '0 12px 8px',
              borderBottom: '1px solid #f0f0f0',
              marginBottom: 8,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 600,
                fontSize: 12,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#475569',
                marginBottom: 8,
              }}
            >
              <EnvironmentOutlined /> Localisation
            </div>
            {Object.entries(productsByRegion).map(([region, count]) => (
              <div key={region}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '3px 0',
                  }}
                >
                  <Checkbox
                    checked={selectedRegions.includes(region)}
                    onChange={() => toggleRegion(region)}
                    style={{ flex: 1 }}
                  >
                    <span style={{ fontSize: 12 }}>{region}</span>
                    <span
                      style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}
                    >
                      ({count})
                    </span>
                  </Checkbox>
                  {region !== 'France entière' &&
                    productsByDepartment[region] &&
                    Object.keys(productsByDepartment[region]).length > 0 && (
                      <DownOutlined
                        style={{
                          fontSize: 10,
                          color: '#94a3b8',
                          cursor: 'pointer',
                          transform: expandedRegions[region]
                            ? 'rotate(180deg)'
                            : 'none',
                          transition: 'transform 0.2s',
                        }}
                        onClick={() => toggleRegionExpand(region)}
                      />
                    )}
                </div>
                {expandedRegions[region] && productsByDepartment[region] && (
                  <div
                    style={{
                      marginLeft: 22,
                      borderLeft: '2px solid #e2e8f0',
                      paddingLeft: 8,
                      marginBottom: 4,
                    }}
                  >
                    {Object.entries(productsByDepartment[region]).map(
                      ([dept, deptCount]) => (
                        <Checkbox
                          key={dept}
                          checked={selectedDepartments.includes(dept)}
                          onChange={() => toggleDepartment(dept)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '2px 0',
                          }}
                        >
                          <span style={{ fontSize: 11 }}>
                            {dept} – {deptNames[dept] || dept}
                          </span>
                          <span
                            style={{
                              fontSize: 10,
                              color: '#94a3b8',
                              marginLeft: 4,
                            }}
                          >
                            ({deptCount})
                          </span>
                        </Checkbox>
                      )
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Catégorie */}
          <div style={{ padding: '0 12px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 600,
                fontSize: 12,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#475569',
                marginBottom: 8,
              }}
            >
              <AppstoreOutlined /> Secteur
            </div>
            {productsByCategory.map(([cat, count]) => (
              <Checkbox
                key={cat}
                checked={selectedCategories.includes(cat)}
                onChange={() => toggleCategory(cat)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '3px 0',
                }}
              >
                <span style={{ fontSize: 12 }}>{cat}</span>
                <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}>
                  ({count})
                </span>
              </Checkbox>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="ag-theme-quartz" style={{ flex: 1, minHeight: 0 }}>
          <AgGridReact
            ref={gridRef}
            rowData={filteredProducts}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            quickFilterText={quickFilter}
            pagination
            paginationPageSize={50}
            paginationPageSizeSelector={[25, 50, 100]}
            rowHeight={58}
            animateRows
            loading={loading}
          />
        </div>
      </div>

      {/* Edit Drawer */}
      <EditProductDrawer
        open={editDrawer.open}
        product={editDrawer.product}
        onClose={() => setEditDrawer({ open: false, product: null })}
        onSaved={fetchProducts}
      />
    </div>
  );
}
